import React from 'react';
import {Box, Typography, Container, Grid, alpha, useTheme, Button} from '@mui/material';
import {useTranslation} from "react-i18next";
import {useAuth0} from "@auth0/auth0-react";
import {Link} from "react-router-dom";
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShowChartIcon from '@mui/icons-material/ShowChart';

const HowItWorksSection = () => {
  const {t} = useTranslation();
  const {loginWithRedirect} = useAuth0();
  const theme = useTheme();

  const steps = [
    {
      number: '01',
      icon: RestaurantMenuIcon,
      title: t('Create your menu'),
      description: t('Add items, photos, prices. Customize your digital menu in just a few clicks. No technical skills needed.'),
    },
    {
      number: '02',
      icon: QrCode2Icon,
      title: t('Share QR code'),
      description: t('Print your unique QR code and place it on tables, at checkout, or anywhere. Customers scan and start ordering instantly.'),
    },
    {
      number: '03',
      icon: ShowChartIcon,
      title: t('Grow your business'),
      description: t('Track orders in real-time, analyze customer behavior, and optimize your offerings with built-in analytics.'),
    },
  ];

  return (
    <Box
      sx={{
        py: {xs: 8, md: 12},
        bgcolor: 'background.paper',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{textAlign: 'center', mb: 8}}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.1em',
              fontSize: '0.875rem',
            }}
          >
            {t("HOW IT WORKS")}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: {xs: '2rem', md: '3rem'},
              fontWeight: 700,
              mt: 1,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t("Launch in 3 simple steps")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.125rem',
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            {t("From setup to serving customers — in minutes, not weeks")}
          </Typography>
        </Box>

        {/* Steps Grid */}
        <Grid container spacing={4}>
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  sx={{
                    position: 'relative',
                    height: '100%',
                    p: 4,
                    borderRadius: '16px',
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  {/* Step Number */}
                  <Typography
                    variant="h6"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 20,
                      fontSize: '3rem',
                      fontWeight: 800,
                      color: alpha(theme.palette.primary.main, 0.1),
                      lineHeight: 1,
                    }}
                  >
                    {step.number}
                  </Typography>

                  {/* Icon */}
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '12px',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <IconComponent
                      sx={{
                        fontSize: 32,
                        color: 'primary.main',
                      }}
                    />
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: 'text.primary',
                    }}
                  >
                    {step.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.7,
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Bottom CTA */}
        <Box
          sx={{
            mt: 8,
            textAlign: 'center',
            p: 4,
            borderRadius: '16px',
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 1,
            }}
          >
            {t("Ready to get started?")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("Join hundreds of businesses already using QRseller")}
          </Typography>
          <Link
            to="/"
            onClick={() => loginWithRedirect({screen_hint: 'signup'})}
            style={{textDecoration: 'none'}}
          >
            <Button
              variant="contained"
              size="large"
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
              }}
            >
              {t("Start Free Trial")}
            </Button>
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorksSection;
